import React, { useState, useEffect, useRef, useCallback } from "react";
// eslint-disable-next-line no-unused-vars
import { motion } from "framer-motion";
import toast from "react-hot-toast";
import { useAuth } from "../../context/AuthContext";
import { useChat } from "../../hooks/useChat"; // NEW HOOK
import { useChatContext } from "../../context/ChatContext";
import { useSearchParams } from "react-router-dom";
import { Feather, Send, Mic, Image, Smile, Lock, Paperclip, X, Menu } from "lucide-react";
import ErrorBoundary from "../../components/ErrorBoundary";
import api from "../../api/axios";
import { redactPII } from "../utils/moderation";

import MessageInput from "../../components/Chat/MessageInput";
import MessageList from "../../components/Chat/MessageList";
import ChatSidebar from "../../components/Chat/ChatSidebar";
import ChatHeader from "../../components/Chat/ChatHeader";
import { CONFIG } from "../../config";
import ContextCard from "../../components/Chat/ContextCard";
import ResourceModal from "../../components/Chat/ResourceModal";
import FeedbackModal from "../../components/Chat/FeedbackModal";
import EmotionRing from "../../components/Chat/EmotionRing";
import SoulLetterEditor from "../../components/Chat/SoulLetterEditor";
import { useLanguage } from "../../context/LanguageContext";

const ASYNC_SYSTEM_MESSAGE =
  "Replies may take time. This space is designed for thoughtful responses.";

const CERT_ACCESS_MESSAGE =
  "This guide requires certified access for your concern.";

export default function ChatCore({
  canRateProvider,
  showEmotionRing,
  fetchSuggestionsEnabled,
  isFocusMode = false,           // Codex can request default focus mode
  customInputAddons = null,      // Codex can inject suggestion chips here
  customEmptyState = null,       // Codex can inject role-specific empty states
  onFocusModeChange = null       // Callback for parent sync
}) {
  const { user } = useAuth();
  const { t } = useLanguage();
  const { onlineUsers } = useChatContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const cidFromUrl = searchParams.get("cid");

  const [selectedConversation, setSelectedConversation] = useState(null);
  const [activeTab, setActiveTab] = useState("Recent");
  const [inputValue, setInputValue] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [isVentMode, setIsVentMode] = useState(false);
  const [isLetterOpen, setIsLetterOpen] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);

  useEffect(() => {
    onFocusModeChange?.(false);
  }, [onFocusModeChange]);

  const [_isTypingFast, setIsTypingFast] = useState(false);
  const keystrokeTimestamps = useRef([]);

  // Local message state (fetched + live)
  const [messagesByConversation, setMessagesByConversation] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("chatMessages")) || {};
    } catch {
      return {};
    }
  });

  // Vent Mode completely isolated local array
  const [ventMessages, setVentMessages] = useState({});

  useEffect(() => {
    const savable = {};
    for (const [cid, msgs] of Object.entries(messagesByConversation)) {
      // PERMANENT FIX: Never save Vent messages to LocalStorage
      savable[cid] = msgs.filter(m => !m.isVent);
    }
    localStorage.setItem("chatMessages", JSON.stringify(savable));
  }, [messagesByConversation]);

  const chatEndRef = useRef(null);
  const conversationId = selectedConversation?.id || null;
  const guide = selectedConversation || null;
  // Ensure isChatAllowed is always true unless explicitly forbidden
  const isChatAllowed = selectedConversation?.isAllowed !== false;

  // 📂 Archive State
  const [archivedIds, setArchivedIds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("chatArchives")) || [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("chatArchives", JSON.stringify(archivedIds));
  }, [archivedIds]);


  /* ================= FETCH CONVERSATIONS ================= */
  const [conversations, setConversations] = useState(() => {
    try {
      const cached = localStorage.getItem("chatListCache");
      return cached ? JSON.parse(cached) : [];
    } catch { return []; }
  });

  // Save to Cache whenever list updates
  useEffect(() => {
    if (conversations.length > 0) {
      localStorage.setItem("chatListCache", JSON.stringify(conversations));
    }
  }, [conversations]);

  useEffect(() => {
    const fetchConversations = async () => {
      try {
        const res = await api.get(`/conversations`);
        if (res.data) {
          const list = res.data.conversations || [];

          // Transform for Sidebar
          const currentUserId = Number(user?.id || user?.userId);
          const formatted = list.map(c => {
            let otherParticipant = null;
            if (c.participants && Array.isArray(c.participants)) {
              otherParticipant = c.participants.find(p => p.user && Number(p.user.id) !== currentUserId) || c.participants[0];
            }
            const other = otherParticipant?.user;

            let displayName = other?.name || "Unknown";
            let displayAvatar = other?.avatar;

            if (other?.role === "SEEKER") {
              const isGhost = other?.seeker?.isAnonymous;
              const ghostName = other?.seeker?.username;
              displayName = ghostName || other.name || "Anonymous Seeker";
              if (isGhost === true) displayAvatar = null;
            }

            const modifiedOther = other ? { ...other, name: displayName, avatar: displayAvatar } : null;

            return {
              id: c.id,
              name: displayName === "Provider" ? "Compassionate Guide" : displayName,
              role: other?.role === "PROVIDER" ? "Guide" : other?.role || "User",
              otherUser: modifiedOther,
              isAllowed: true,
              isArchived: archivedIds.includes(c.id)
            };
          });
          setConversations(formatted);
        }
      } catch (err) {
        console.error("Failed to load chats", err);
      }
    };

    if (user) fetchConversations();
  }, [user, archivedIds]);

  // Reactive CID sync
  useEffect(() => {
    if (cidFromUrl && conversations.length > 0) {
      const target = conversations.find(c => c.id === Number(cidFromUrl));
      if (target && (!selectedConversation || selectedConversation.id !== target.id)) {
        setSelectedConversation(target);
        setShowMobileSidebar(false);
      }
    } else if (!cidFromUrl && selectedConversation) {
      // Clear selection if URL has no conversation ID
      setSelectedConversation(null);
    }
  }, [cidFromUrl, conversations, selectedConversation]);

  // Handle active chat notification clearing
  useEffect(() => {
    if (conversationId) {
      api.put(`/notifications/read-by-chat/${conversationId}`)
        .then(() => {
          window.dispatchEvent(new CustomEvent("sync_notifications"));
        })
        .catch(() => {});
    }
  }, [conversationId]);

  /* ================= HISTORY & PAGINATION ================= */
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  const processMessages = useCallback((currentList, newMessages) => {
    const unique = new Map();
    currentList.forEach(m => unique.set(m.id || m.tempId, m));
    newMessages.forEach(m => unique.set(m.id || m.tempId, m));
    return Array.from(unique.values()).sort((a, b) =>
      new Date(a.createdAt || a.timestamp) - new Date(b.createdAt || b.timestamp)
    );
  }, []);

  const fetchMessages = useCallback(async (cid, cursor = null) => {
    try {
      setIsLoadingHistory(true);
      let url = `/conversations/${cid}/messages?limit=50`;
      if (cursor) url += `&cursor=${cursor}`;

      const res = await api.get(url);

      if (res.data) {
        const fetchedMsgs = res.data.messages || [];

        const formatted = fetchedMsgs
          .filter(m => !m.isVent)
          .map(m => ({
            id: m.id,
            text: m.content,
            from: m.senderId === user.id ? "user" : "guide",
            type: m.type,
            isVent: m.isVent,
            timestamp: m.createdAt,
            senderId: m.senderId
          }));

        setMessagesByConversation(prev => {
          const current = prev[cid] || [];
          const merged = processMessages(current, formatted);
          return { ...prev, [cid]: merged };
        });

        setHasMore(fetchedMsgs.length === 50);
      }
    } catch (err) {
      console.error("Fetch history failed", err);
    } finally {
      setIsLoadingHistory(false);
    }
  }, [user, processMessages]);

  useEffect(() => {
    if (conversationId) {
      setHasMore(true);
      fetchMessages(conversationId);
    }
  }, [conversationId, fetchMessages]);

  useEffect(() => {
    if (!isVentMode && conversationId) {
      setVentMessages(prev => {
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
    }
  }, [isVentMode, conversationId]);

  const handleLoadMore = () => {
    if (!hasMore || isLoadingHistory || !conversationId) return;
    const msgs = messagesByConversation[conversationId] || [];
    const oldest = msgs[0];
    if (oldest?.id) fetchMessages(conversationId, oldest.id);
  };

  /* ================= SUGGESTIONS ================= */
  const [suggestedProviders, setSuggestedProviders] = useState([]);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const res = await api.get(`/categories/matches`);
        if (res.data) {
          const data = res.data;
          const mapped = data.map(p => ({
            id: `new-${p.userId}`,
            providerId: p.id,
            name: (p.user?.name && p.user.name !== "Provider") ? p.user.name : (p.name || "Compassionate Guide"),
            otherUser: p.user,
            role: "PROVIDER",
            isNew: true,
            isAllowed: true,
            isArchived: false
          }));
          setSuggestedProviders(mapped);
        }
      } catch (err) { console.error("Failed suggestions", err); }
    };
    if (fetchSuggestionsEnabled) fetchSuggestions();
  }, [fetchSuggestionsEnabled]);

  const handleSelectGuide = async (item) => {
    if (item.isNew) {
      try {
        const res = await api.post(`/conversations/start`, { providerId: item.providerId });
        if (res.data) {
          const data = res.data;
          const realConv = {
            ...item,
            id: data.conversationId,
            isNew: false,
            name: item.name,
            otherUser: item.otherUser
          };
          setConversations(prev => {
            if (prev.find(c => c.id === realConv.id)) return prev;
            return [realConv, ...prev];
          });
          setSelectedConversation(realConv);
          setSearchParams({ cid: realConv.id });
          setActiveTab("Recent");
        }
      } catch (err) {
        toast.error(err.response?.data?.error || "Could not start chat");
      }
    } else {
      setSelectedConversation(item);
      setSearchParams({ cid: item.id });
    }
    setShowMobileSidebar(false);
  };

  /* ================= SOCKET INTEGRATION ================= */
  const handleReceiveMessage = useCallback((message) => {
    if (!message?.conversationId) return;
    const commonUpdate = { id: message.id, status: "sent", timestamp: message.createdAt || Date.now() };

    if (message.isVent) {
      setVentMessages((prev) => {
        const currentVent = prev[message.conversationId] || [];
        if (message.senderId === user.id) {
          const index = currentVent.findIndex(m => m.from === "user" && m.status === "sending");
          if (index !== -1) {
            const nextDocs = [...currentVent];
            nextDocs[index] = { ...currentVent[index], ...commonUpdate };
            return { ...prev, [message.conversationId]: nextDocs };
          }
        }
        const newMsg = { id: message.id, text: message.content, from: message.senderId === user.id ? "user" : "guide", type: message.type, isVent: true, timestamp: message.createdAt || Date.now(), senderId: message.senderId };
        return { ...prev, [message.conversationId]: processMessages(currentVent, [newMsg]) };
      });
      return; 
    }

    setMessagesByConversation((prev) => {
      const current = prev[message.conversationId] || [];
      if (message.senderId === user.id) {
        const index = current.findIndex(m => m.from === "user" && m.status === "sending");
        if (index !== -1) {
          const nextDocs = [...current];
          nextDocs[index] = { ...current[index], ...commonUpdate };
          return { ...prev, [message.conversationId]: nextDocs };
        }
      }
      const newMsg = { id: message.id, text: message.content, from: message.senderId === user.id ? "user" : "guide", type: message.type, isVent: false, timestamp: message.createdAt || Date.now(), senderId: message.senderId };
      return { ...prev, [message.conversationId]: processMessages(current, [newMsg]) };
    });
  }, [user, processMessages]);

  const { sendMessage, setTyping, connected } = useChat({
    conversationId,
    onMessage: handleReceiveMessage,
    onTyping: (p) => { if (p?.userId !== user.id) setIsTyping(!!p?.isTyping); },
    onPresence: () => {},
    onMoodUpdate: (p) => {
      if (!p?.userId || !p?.mood) return;
      const targetUserId = Number(p.userId);
      setConversations(prev => prev.map(c => c.otherUser?.id === targetUserId ? { ...c, otherUser: { ...c.otherUser, currentMood: p.mood } } : c));
      if (selectedConversation?.otherUser?.id === targetUserId) {
        setSelectedConversation(prev => ({ ...prev, otherUser: { ...prev.otherUser, currentMood: p.mood } }));
      }
    }
  });

  const { updateMood } = useChatContext();
  const isOnline = !!guide?.otherUser?.id && onlineUsers.has(guide.otherUser.id);

  /* ================= UI HANDLERS ================= */
  useEffect(() => {
    if (!conversationId || !setTyping) return;
    if (inputValue.length > 0) {
      setTyping(true);
      const now = Date.now();
      keystrokeTimestamps.current.push(now);
      if (keystrokeTimestamps.current.length > 10) keystrokeTimestamps.current.shift();
    }
    const timer = setTimeout(() => { setTyping(false); keystrokeTimestamps.current = []; }, 2000);
    return () => clearTimeout(timer);
  }, [inputValue, conversationId, setTyping]);

  const handleSend = useCallback(() => {
    if (!isChatAllowed || !inputValue.trim()) return;
    const safeText = redactPII(inputValue.trim());
    const tempId = `temp-${Date.now()}`;
    const optimisticMsg = { id: tempId, tempId: tempId, text: safeText, from: "user", type: "TEXT", isVent: isVentMode, timestamp: Date.now(), status: "sending" };
    if (isVentMode) setVentMessages(prev => ({ ...prev, [conversationId]: [...(prev[conversationId] || []), optimisticMsg] }));
    else setMessagesByConversation(prev => ({ ...prev, [conversationId]: [...(prev[conversationId] || []), optimisticMsg] }));
    sendMessage({ content: safeText, type: "TEXT", isVent: isVentMode });
    setInputValue("");
  }, [inputValue, conversationId, sendMessage, isChatAllowed, isVentMode]);

  const handleSendMedia = async (file) => {
    if (!file || !conversationId) return;
    const toastId = toast.loading("Sending attachment...");
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await api.post(`/conversations/upload`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const data = res.data;
      toast.success("Sent!", { id: toastId });
      setMessagesByConversation(prev => ({ ...prev, [conversationId]: [...(prev[conversationId] || []), { text: data.url, from: "user", type: data.type, timestamp: Date.now(), status: "sending" }] }));
      sendMessage({ content: data.url, type: data.type });
    } catch { toast.error("Failed to send file", { id: toastId }); }
  };

  const handleSelectResource = (article) => {
    const payload = JSON.stringify({ id: article.id, title: article.title, author: article.author?.name, coverImage: article.coverImage, slug: article.slug });
    setMessagesByConversation(prev => ({ ...prev, [conversationId]: [...(prev[conversationId] || []), { id: `temp-res-${Date.now()}`, text: payload, from: "user", type: "RESOURCE", timestamp: Date.now(), status: "sending" }] }));
    sendMessage({ content: payload, type: "RESOURCE" });
    toast.success("Resource shared");
  };

  const toggleArchiveConversation = () => {
    if (!conversationId) return;
    const isCurrentlyArchived = archivedIds.includes(conversationId);
    if (isCurrentlyArchived) {
      setArchivedIds(prev => prev.filter(id => id !== conversationId));
      toast.success("Conversation unarchived");
      setActiveTab("Recent");
    } else {
      setArchivedIds(prev => [...prev, conversationId]);
      toast.success("Conversation archived");
      setActiveTab("Archived");
      setSelectedConversation(null);
      setSearchParams({});
    }
  };

  const [isResourceModalOpen, setResourceModalOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  if (!user) return null;

  /* ================= RENDER ================= */
  return (
    <div className="flex h-[calc(100dvh-64px)] overflow-hidden bg-spiritual-white dark:bg-gray-950/20 relative">
      {showMobileSidebar && (
        <div className="md:hidden fixed inset-0 bg-black/50 z-40 backdrop-blur-sm" onClick={() => setShowMobileSidebar(false)} />
      )}

      {/* Desktop & Mobile List View Logic */}
      <aside className="hidden md:flex flex-col w-80 flex-shrink-0 min-h-0 h-full bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 overflow-hidden">
        <ErrorBoundary>
          <ChatSidebar activeTab={activeTab} setActiveTab={setActiveTab} selectedGuide={selectedConversation} setSelectedGuide={handleSelectGuide} onClearConversation={toggleArchiveConversation} chatList={conversations} suggestedList={suggestedProviders.filter(sp => !conversations.some(existing => existing.otherUser?.id === sp.providerId))} onCloseMobile={() => setShowMobileSidebar(false)} />
        </ErrorBoundary>
      </aside>

      {!selectedConversation && (
        <aside className="md:hidden flex flex-col w-full h-full bg-white dark:bg-gray-900 overflow-hidden">
          <ErrorBoundary>
            <ChatSidebar activeTab={activeTab} setActiveTab={setActiveTab} selectedGuide={selectedConversation} setSelectedGuide={handleSelectGuide} onClearConversation={toggleArchiveConversation} chatList={conversations} suggestedList={suggestedProviders.filter(sp => !conversations.some(existing => existing.otherUser?.id === sp.providerId))} onCloseMobile={() => setShowMobileSidebar(false)} />
          </ErrorBoundary>
        </aside>
      )}

      <aside className={`md:hidden fixed inset-y-0 left-0 z-50 w-80 h-full bg-white dark:bg-gray-900 border-r border-gray-100 dark:border-gray-800 transition-transform duration-300 ease-in-out ${showMobileSidebar && selectedConversation ? "translate-x-0 shadow-2xl" : "-translate-x-full"}`}>
        <ErrorBoundary>
          <ChatSidebar activeTab={activeTab} setActiveTab={setActiveTab} selectedGuide={selectedConversation} setSelectedGuide={handleSelectGuide} onClearConversation={toggleArchiveConversation} chatList={conversations} suggestedList={suggestedProviders.filter(sp => !conversations.some(existing => existing.otherUser?.id === sp.providerId))} onCloseMobile={() => setShowMobileSidebar(false)} />
        </ErrorBoundary>
      </aside>

      <div className={`flex-1 flex flex-col min-h-0 relative w-full ${!selectedConversation ? 'hidden md:flex' : ''}`}>
        <ErrorBoundary>
          {selectedConversation ? (
            <>
              <div className="relative z-30 bg-white dark:bg-gray-950/50 backdrop-blur-md sticky top-0">
                <ChatHeader guide={guide} isOnline={isOnline} isConnected={connected} isArchived={archivedIds.includes(conversationId)} onToggleArchive={toggleArchiveConversation} onRate={canRateProvider ? () => setIsFeedbackOpen(true) : undefined} currentMood={user?.currentMood} onUpdateMood={showEmotionRing ? (mood) => updateMood(mood) : undefined} onToggleSidebar={() => setShowMobileSidebar(!showMobileSidebar)} isSidebarOpen={showMobileSidebar} onBack={() => setSearchParams({})} />
              </div>
              <ContextCard user={guide?.otherUser} currentUser={user} />
              <div className={`flex-1 flex flex-col overflow-hidden relative ${isVentMode ? "bg-black/5" : ""}`}>
                {isVentMode && <div className="absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-gray-900/10 to-transparent pointer-events-none z-10" />}
                <MessageList currentMessages={isVentMode ? (ventMessages[conversationId] || []) : (messagesByConversation[conversationId] || [])} chatEndRef={chatEndRef} user={user} guide={guide} onLoadMore={handleLoadMore} hasMore={isVentMode ? false : hasMore} isLoading={isLoadingHistory} />
              </div>
              <div className="w-full flex justify-center pb-2">{customInputAddons}</div>
              {isChatAllowed ? (
                <div className="relative z-20">
                  <MessageInput inputValue={inputValue} setInputValue={setInputValue} handleSend={handleSend} handleSendMedia={handleSendMedia} handleOpenResource={() => setResourceModalOpen(true)} disabled={false} isVentMode={isVentMode} setIsVentMode={setIsVentMode} onOpenLetter={() => setIsLetterOpen(true)} hasUnreadVent={!isVentMode && ((ventMessages[conversationId] || []).length > 0)} />
                </div>
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 italic border-t bg-gray-50 dark:bg-gray-800 z-10 text-center">{CERT_ACCESS_MESSAGE}</div>
              )}
            </>
          ) : customEmptyState ? (
            <div className="relative h-full w-full">{customEmptyState}</div>
          ) : (
            <div className="hidden md:flex h-full flex-col items-center justify-center text-center p-8 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm">
              <motion.div animate={{ scale: [1, 1.05, 1], opacity: [0.7, 1, 0.7] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="w-24 h-24 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-800/50 backdrop-blur-sm rounded-full flex items-center justify-center mb-6 shadow-sm ring-1 ring-indigo-100/30 dark:ring-gray-700/30">
                <Feather className="w-12 h-12 text-brand-blue opacity-80" />
              </motion.div>
              <h2 className="text-2xl font-display font-medium text-gray-800 dark:text-gray-100 mb-2 truncate">
                {t("chat_intro_title")}
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-sm leading-relaxed text-sm sm:text-base px-4">
                {t("chat_intro_body")}
              </p>
            </div>
          )}
        </ErrorBoundary>
      </div>

      <ResourceModal isOpen={isResourceModalOpen} onClose={() => setResourceModalOpen(false)} onSelectResource={handleSelectResource} />
      <FeedbackModal isOpen={isFeedbackOpen} onClose={() => setIsFeedbackOpen(false)} provider={guide?.otherUser} />
      <SoulLetterEditor isOpen={isLetterOpen} onClose={() => setIsLetterOpen(false)} onSend={handleSend} />
    </div>
  );
}
